// src/app/pages/issues/issues.component.ts

import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  ElementRef,
  HostListener,
  Inject,
  PLATFORM_ID
} from '@angular/core';

import {
  isPlatformBrowser,
  CommonModule,
  ViewportScroller
} from '@angular/common';

import {
  IssuesService,
  Issue
} from '../../services/issues.service';

import { SeoService } from '../../services/seo.service';

import {
  FormsModule,
  ReactiveFormsModule
} from '@angular/forms';

import { RouterModule } from '@angular/router';

interface IssueSection {
  key: string;
  title: string;
}

interface GroupedIssueSection {
  key: string;
  title: string;
  issues: Issue[];
}

// Single source of truth for section identity and display order.
const ISSUE_SECTIONS: IssueSection[] = [
  {
    key: 'civil_liberties',
    title: 'Civil Liberties & Privacy'
  },
  {
    key: 'personal_freedom',
    title: 'Personal Freedom'
  },
  {
    key: 'economic_freedom',
    title: 'Economic Freedom'
  },
  {
    key: 'government_accountability',
    title: 'Government Accountability'
  },
  {
    key: 'foreign_policy',
    title: 'Foreign Policy'
  },
  {
    key: 'economic_security',
    title: 'Economic Security'
  },
  {
    key: 'civil_rights_equality',
    title: 'Civil Rights & Equality'
  },
  {
    key: 'immigration',
    title: 'Immigration'
  },
  {
    key: 'justice_system',
    title: 'Justice System'
  },
  {
    key: 'tech_infrastructure',
    title: 'Technology & Infrastructure'
  },
  {
    key: 'environment',
    title: 'Environment'
  },
  {
    key: 'foundational',
    title: 'Foundational'
  }
];

@Component({
  selector: 'app-issues',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule
  ],
  templateUrl: './issues.component.html',
  styleUrls: ['./issues.component.css']
})
export class IssuesComponent
  implements OnInit, OnDestroy {

  issues: Issue[] = [];
  groupedIssues: GroupedIssueSection[] = [];

  loading = true;
  showBackToTop = false;

  private readonly baseUrl =
    'https://www.lydialynnchristensen.com';

  private readonly issuesPageId =
    `${this.baseUrl}/issues#collection`;

  private readonly issueListId =
    `${this.baseUrl}/issues#issue-list`;

  constructor(
    private issuesService: IssuesService,
    private cdRef: ChangeDetectorRef,
    private seo: SeoService,
    private hostRef: ElementRef<HTMLElement>,
    private viewportScroller: ViewportScroller,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  ngOnInit(): void {

    this.seo.update(
      'Issues & Platform',
      'Lydia Christensen\'s platform on the Great Lakes, healthcare, LGBTQ+ rights, rural Michigan, and economic opportunity.',
      'issues'
    );

    /*
     * Load the actual issue data first.
     *
     * The structured data is generated from the same
     * Issue objects that the page displays.
     */
    this.issuesService.fetchIssues();

    this.issuesService.getIssues().subscribe({

      next: (issues) => {

        this.issues = issues;

        this.groupedIssues =
          this.groupByCategory(issues);

        this.loading = false;

        this.setStructuredData(issues);

        this.cdRef.detectChanges();

        this.syncHeaderOffset();
      },

      error: (error) => {

        console.error(
          'Error fetching issues:',
          error
        );

        this.loading = false;

        /*
         * We don't generate an issue list if the
         * actual issue data wasn't loaded.
         */
        this.setCollectionStructuredData();
      }
    });
  }

  ngOnDestroy(): void {

    /*
     * Reset the global ViewportScroller offset when
     * leaving this page.
     */
    this.viewportScroller.setOffset([0, 0]);
  }

  // ─── Scroll handling ────────────────────────────────────────────────────────

  @HostListener('window:resize')
  onWindowResize(): void {
    this.syncHeaderOffset();
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {

    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.showBackToTop =
      window.scrollY > 600;
  }

  scrollToTop(): void {

    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }

  // ─── Issue helpers ──────────────────────────────────────────────────────────

  isOptInFlagIssue(issue: Issue): boolean {

    return /medicaid|healthcare|social security/i
      .test(issue.title);
  }

  private groupByCategory(
    issues: Issue[]
  ): GroupedIssueSection[] {

    const grouped =
      ISSUE_SECTIONS.map(section => ({

        key: section.key,

        title: section.title,

        issues: issues
          .filter(
            issue =>
              issue.category === section.key
          )
          .sort(
            (a, b) =>
              a.sort_order - b.sort_order
          )
      }));

    const knownIssueIds =
      new Set(
        grouped.flatMap(
          section =>
            section.issues.map(
              issue => issue.id
            )
        )
      );

    const uncategorizedIssues =
      issues
        .filter(
          issue =>
            !knownIssueIds.has(issue.id)
        )
        .sort(
          (a, b) =>
            a.sort_order - b.sort_order
        );

    if (uncategorizedIssues.length > 0) {

      grouped.push({
        key: 'other',
        title: 'Other Issues',
        issues: uncategorizedIssues
      });
    }

    return grouped.filter(
      section =>
        section.issues.length > 0
    );
  }

  // ─── Structured data ───────────────────────────────────────────────────────

  private setStructuredData(
    issues: Issue[]
  ): void {

    const issueItems =
      issues.map(
        (issue, index) =>
          this.buildIssueListItem(
            issue,
            index
          )
      );

    this.seo.setStructuredData({

      "@context": "https://schema.org",

      "@graph": [

        /*
         * The actual Issues page.
         */
        {
          "@type": "CollectionPage",

          "@id": this.issuesPageId,

          "url":
            `${this.baseUrl}/issues`,

          "name":
            "Lydia Christensen Issues & Platform",

          "description":
            "Lydia Christensen's positions and policy platform.",

          "isPartOf": {
            "@id": this.seo.websiteId
          },

          "about": {
            "@id": this.seo.lydiaId
          },

          "publisher": {
            "@id": this.seo.campaignId
          },

          "mainEntity": {
            "@id": this.issueListId
          }
        },

        /*
         * The complete list of individual issue pages.
         */
        {
          "@type": "ItemList",

          "@id": this.issueListId,

          "name":
            "Lydia Christensen Policy Platform",

          "numberOfItems":
            issues.length,

          "itemListOrder":
            "https://schema.org/ItemListOrderAscending",

          "itemListElement":
            issueItems
        }

      ]
    });
  }

  /*
   * Collection-level fallback if the issue service
   * fails before the actual issue data is available.
   */
  private setCollectionStructuredData(): void {

    this.seo.setStructuredData({

      "@context": "https://schema.org",

      "@type": "CollectionPage",

      "@id": this.issuesPageId,

      "url":
        `${this.baseUrl}/issues`,

      "name":
        "Lydia Christensen Issues & Platform",

      "description":
        "Lydia Christensen's positions and policy platform.",

      "isPartOf": {
        "@id": this.seo.websiteId
      },

      "about": {
        "@id": this.seo.lydiaId
      },

      "publisher": {
        "@id": this.seo.campaignId
      }
    });
  }

  private buildIssueListItem(
    issue: Issue,
    index: number
  ): any {

    /*
     * Use the same canonical URL logic as the
     * individual issue-detail page.
     */
    const canonicalSlug =
      issue.slug ||
      String(issue.id);

    const issueUrl =
      `${this.baseUrl}/issues/${canonicalSlug}`;

    return {

      "@type": "ListItem",

      "position":
        index + 1,

      "url":
        issueUrl
    };
  }

  // ─── Header / fragment scrolling ───────────────────────────────────────────

  private syncHeaderOffset(): void {

    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const header =
      document.querySelector('header');

    const headerHeight =
      header?.getBoundingClientRect().height ?? 0;

    this.hostRef.nativeElement.style
      .setProperty(
        '--issues-header-h',
        `${headerHeight}px`
      );

    this.viewportScroller.setOffset([
      0,
      headerHeight
    ]);
  }
}

